import React, { SyntheticEvent, useState } from 'react';

import { createPhoneManager } from '../../../di';
import { useEffectOnUpdate, useErrorCallback } from '../../../hooks';
import { Errors as ValidationErrors, RouteDictionary } from '../../../utils';
import { ymAsync } from '../../../utils/functions/ymWrapper';
import { AuthInput } from '../../../views/auth/Enter/Enter';
import {
  BoldText,
  Button,
  ColoredText,
  CustomLink,
  InputErrors,
  InputNumber,
  PrimaryButton,
} from '../../common';
import { PrimaryButtonColor } from '../../common/buttons/decorators/PrimaryButton/PrimaryButton';
import { Checkbox } from '../../common/form';
import { TextColor } from '../../common/typography/texts/ColoredText/ColoredText';

import './SendCode.scoped.scss';

type Props = {
  input: AuthInput;
  onBack: () => void;
  onSubmit: () => void;
};

type FormErrors = {
  agreeRules?: ValidationErrors;
};

const SendCode = ({ input, onBack, onSubmit }: Props) => {
  const [agreeRules, setAgreeRules] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const handleError = useErrorCallback();

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (!agreeRules) {
      errors.agreeRules = ['Примите правила использования'];
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffectOnUpdate(() => {
    validateForm();
  }, [agreeRules]);

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    ymAsync('registration-get-code-button');
    setLoading(true);
    try {
      if (validateForm()) {
        const phoneManager = await createPhoneManager();
        await phoneManager.createPhone(input.phone);
        onSubmit();
      }
    } catch (err) {
      if (err instanceof Error) {
        await handleError(err);
      }
    } finally {
      ymAsync('investor_register_get_code');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="send-code">
        <PrimaryButton color={PrimaryButtonColor.GRAY}>
          <Button onClick={onBack}>Назад</Button>
        </PrimaryButton>
        <BoldText>
          <div className="send-code__heading">Вход / регистрация</div>
        </BoldText>
        <div className="send-code__text">
          <span className="d-block">Такой номер нам еще не известен.</span>
          <span className="d-block">
            Если вы не ошиблись, мы вышлем СМС с кодом для регистрации
          </span>
        </div>
        <form className="form" onSubmit={handleSubmit}>
          <div className="form__group">
            <InputNumber phone={input.phone} valid />
          </div>
          <div className="form__group">
            <Checkbox
              id="agree-rules"
              checked={agreeRules}
              onChange={() => setAgreeRules((agree) => !agree)}
            >
              Запрашивая код, вы даете согласие с{' '}
              <ColoredText color={TextColor.PRIMARY_GREEN}>
                <CustomLink to={RouteDictionary.RULES_REGISTRATION} target={'_blank'}>
                  Правилами регистрации
                </CustomLink>
              </ColoredText>{' '}
              Онлайн-сервиса Кономика
            </Checkbox>
            {errors.agreeRules && <InputErrors errors={errors.agreeRules} />}
          </div>
          <div className={'form__group'}>
            <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
              <Button type="submit" disabled={!agreeRules || loading}>
                Получить код
              </Button>
            </PrimaryButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default SendCode;
